/**
 * Read-only data loader that fetches importer-backed tables from Supabase
 * and maps them into the exact shapes App.tsx already consumes.
 *
 * This module NEVER writes to Supabase — write flows are handled by the
 * importer pipeline.
 */

import { selectAll } from "./client";

// ─── Supabase row types (match migration schema) ────────────────────────────

interface SupaIngredient {
  id: string;
  common_name: string;
  korean_name: string | null;
  protein_per_100g: number;
  digestibility: number | null;
  cost_per_kg: number;
  fat: number;
  satfat: number;
  transfat: number;
  cholesterol: number;
  carbs: number;
  fiber: number;
  sugar: number;
  sodium: number;
  moisture: number;
  vitamin_d: number;
  calcium: number;
  iron: number;
  potassium: number;
  fda_name: string | null;
  color: string;
  aa_his: number;
  aa_ile: number;
  aa_leu: number;
  aa_lys: number;
  aa_mc: number;
  aa_pt: number;
  aa_thr: number;
  aa_trp: number;
  aa_val: number;
  supplier: string | null;
  sub_ingredients: string | null;
  notes_spec_ref: string | null;
  // Fatty acids (g/100g)
  fa_ala: number;
  fa_la: number;
  fa_oleic: number;
  fa_palmitic: number;
  fa_stearic: number;
  // Fiber fractions (g/100g)
  fiber_insoluble: number;
  fiber_soluble: number;
  resistant_starch: number;
  // BCAA (mg/g protein, derived)
  bcaa_mg_per_g: number;
  // Reference digestibility
  ref_digest_product: string | null;
  ref_digest_value: number | null;
  ref_digest_source: string | null;
  // Per-field data sources
  data_sources: Record<string, string> | null;
}

interface SupaFormula {
  formula_code: string;
  display_name: string;
  sheet_gid: string | null;
  sheet_name: string | null;
  piece_weight_g: number;
  serving_size_g: number;
  dough_ratio_pct: number;
  ratio_mode: string | null;
  final_moisture_mode: string | null;
  final_moisture_whole: number | null;
  final_moisture_dough: number | null;
  final_moisture_filling: number | null;
}

interface SupaComponent {
  formula_code: string;
  component: string;
  ingredient_id: string | null; // NULL on row_type='process' rows
  pct_of_component: number; // fraction in (0, 1]
  row_type: string | null; // 'process' rows carry no ingredient and are skipped
}

// ─── App-side shapes (what App.tsx expects) ─────────────────────────────────

export interface AppIngredient {
  id: string;
  korean_name?: string;
  /** Authoritative protein form for the Golden Ratio check. Nullable: NULL means
   *  not yet classified, and the check falls back to a marked guess. */
  protein_class?: string | null;
  tags?: string[] | null;
  protein_per_100g: number;
  digestibility: number | null;
  cost_per_kg: number;
  fat: number;
  satfat: number;
  transfat: number;
  cholesterol: number;
  carbs: number;
  fiber: number;
  sugar: number;
  sodium: number;
  moisture: number;
  vitaminD: number;
  calcium: number;
  iron: number;
  potassium: number;
  fdaName: string;
  color: string;
  amino_acids: {
    histidine: number;
    isoleucine: number;
    leucine: number;
    lysine: number;
    methionine_cysteine: number;
    phenylalanine_tyrosine: number;
    threonine: number;
    tryptophan: number;
    valine: number;
  };
  supplier?: string;
  sub_ingredients?: string;
  notes_spec_ref?: string;
  // Fatty acids (g/100g)
  fa_ala: number;
  fa_la: number;
  fa_oleic: number;
  fa_palmitic: number;
  fa_stearic: number;
  // Fiber fractions (g/100g)
  fiber_insoluble: number;
  fiber_soluble: number;
  resistant_starch: number;
  // BCAA (mg/g protein, derived)
  bcaa_mg_per_g: number;
  // Reference digestibility
  ref_digest_product?: string;
  ref_digest_value?: number | null;
  ref_digest_source?: string;
  // Per-field data sources
  data_sources?: Record<string, string> | null;
}

export interface AppFormula {
  code: string;
  displayName: string;
  gid: string;
  sheetName: string;
  pieceWeight: number;
  servingSize: number;
  doughRatio: number;
  ratioMode: "as_mixed" | "finished";
  finalMoistureMode: string | null;
  finalMoistureWhole: number | null;
  finalMoistureDough: number | null;
  finalMoistureFilling: number | null;
}

/** Dict keyed by common name — same shape as DB_INGREDIENTS in App.tsx */
export type IngredientsMap = Record<string, AppIngredient>;

/** Map from ingredient id → common name */
export type IdToNameMap = Record<string, string>;

// ─── Mappers ────────────────────────────────────────────────────────────────

function mapIngredient(row: SupaIngredient): AppIngredient {
  return {
    id: row.id,
    korean_name: row.korean_name || "",
    protein_class: (row as any).protein_class ?? null,
    tags: (row as any).tags ?? null,
    protein_per_100g: row.protein_per_100g,
    digestibility: row.digestibility,
    cost_per_kg: row.cost_per_kg,
    fat: row.fat,
    satfat: row.satfat,
    transfat: row.transfat,
    cholesterol: row.cholesterol,
    carbs: row.carbs,
    fiber: row.fiber,
    sugar: row.sugar,
    sodium: row.sodium,
    moisture: row.moisture,
    vitaminD: row.vitamin_d,
    calcium: row.calcium,
    iron: row.iron,
    potassium: row.potassium,
    fdaName: row.fda_name || row.common_name,
    color: row.color || "#888888",
    amino_acids: {
      histidine: row.aa_his,
      isoleucine: row.aa_ile,
      leucine: row.aa_leu,
      lysine: row.aa_lys,
      methionine_cysteine: row.aa_mc,
      phenylalanine_tyrosine: row.aa_pt,
      threonine: row.aa_thr,
      tryptophan: row.aa_trp,
      valine: row.aa_val,
    },
    supplier: row.supplier || "",
    sub_ingredients: row.sub_ingredients || "",
    notes_spec_ref: row.notes_spec_ref || "",
    fa_ala: row.fa_ala,
    fa_la: row.fa_la,
    fa_oleic: row.fa_oleic,
    fa_palmitic: row.fa_palmitic,
    fa_stearic: row.fa_stearic,
    fiber_insoluble: row.fiber_insoluble,
    fiber_soluble: row.fiber_soluble,
    resistant_starch: row.resistant_starch,
    bcaa_mg_per_g: row.bcaa_mg_per_g,
    ref_digest_product: row.ref_digest_product || "",
    ref_digest_value: row.ref_digest_value,
    ref_digest_source: row.ref_digest_source || "",
    data_sources: row.data_sources,
  };
}

function mapFormula(row: SupaFormula): AppFormula {
  return {
    code: row.formula_code,
    displayName: row.display_name,
    gid: row.sheet_gid || "",
    sheetName: row.sheet_name || "",
    pieceWeight: row.piece_weight_g,
    servingSize: row.serving_size_g,
    doughRatio: row.dough_ratio_pct,
    ratioMode: (row.ratio_mode === "finished" ? "finished" : "as_mixed"),
    finalMoistureMode: row.final_moisture_mode,
    finalMoistureWhole: row.final_moisture_whole,
    finalMoistureDough: row.final_moisture_dough,
    finalMoistureFilling: row.final_moisture_filling,
  };
}

// ─── Public loader ──────────────────────────────────────────────────────────

export interface SupabaseData {
  ingredients: IngredientsMap;
  idToName: IdToNameMap;
  formulas: AppFormula[];
}

/**
 * Load all importer-backed data from Supabase and return it in the shapes
 * App.tsx already consumes.  Throws on any fetch/mapping failure — the
 * caller is responsible for falling back to the current local/demo path.
 */
export async function loadSupabaseData(): Promise<SupabaseData> {
  const [ingRows, fmRows] = await Promise.all([
    selectAll<SupaIngredient>("ingredients", "*", "id", { is_active: true }),
    selectAll<SupaFormula>("formulas", "*", "formula_code", { is_active: true }),
  ]);

  // Build ingredients map keyed by common_name (same as DB_INGREDIENTS).
  // Also index by id so the calculation finds ingredients regardless of
  // whether the formula key resolved to a common name or a raw id.
  const ingredients: IngredientsMap = {};
  const idToName: IdToNameMap = {};

  for (const row of ingRows) {
    const commonName = row.common_name;
    const mapped = mapIngredient(row);
    ingredients[commonName] = mapped;
    idToName[row.id] = commonName;

    // Alternate key: raw id (e.g. "WHEAT_FLOUR") so that unresolved
    // component references still find the right nutrient record.
    if (row.id && row.id !== commonName) {
      ingredients[row.id] = mapped;
    }
  }

  const formulas = fmRows.map(mapFormula);

  return { ingredients, idToName, formulas };
}

/**
 * Load formula components for a specific formula and split into
 * dough / filling maps using percentage values (0–100) as App.tsx expects.
 *
 * pct_of_component is stored as a fraction in (0,1] in Supabase;
 * we multiply by 100 here to match the app's internal representation.
 */
export async function loadFormulaComponents(
  formulaCode: string,
  idToName: IdToNameMap,
): Promise<{ dough: Record<string, number>; filling: Record<string, number> }> {
  const rows = await selectAll<SupaComponent>(
    "formula_components",
    "*",
    "id",
    { formula_code: formulaCode },
  );

  const dough: Record<string, number> = {};
  const filling: Record<string, number> = {};

  for (const row of rows) {
    // Skip process rows (NULL ingredient_id / pct) — they would otherwise
    // produce a bogus "null" ingredient with 0 pct in the maps below.
    if (row.row_type === "process" || !row.ingredient_id) continue;
    const commonName = idToName[row.ingredient_id] || row.ingredient_id;
    const pct = row.pct_of_component * 100; // fraction → percentage
    if (row.component === "DOUGH") dough[commonName] = pct;
    if (row.component === "FILLING") filling[commonName] = pct;
  }

  return { dough, filling };
}
